import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://www.sotsekadvocacia.com.br";
const JSONLD_ATTR = "data-seohead-jsonld";

export function SEOHead({
  title,
  description,
  canonicalPath,
  ogImage = "/og-image.png",
  ogType = "website",
  jsonLd,
}: SEOHeadProps) {
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;

  useEffect(() => {
    document.title = title;

    const updateTag = (
      selector: string,
      attribute: string,
      value: string,
      tagName: "meta" | "link" = "meta",
    ) => {
      let element = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;

      if (!element) {
        element = document.createElement(tagName);
        if (tagName === "meta") {
          const attr = selector.includes("property=") ? "property" : "name";
          const attrValue = selector.match(/(?:name|property)="([^"]+)"/)?.[1];
          if (attrValue) {
            (element as HTMLMetaElement).setAttribute(attr, attrValue);
          }
        } else if (tagName === "link") {
          const relValue = selector.match(/rel="([^"]+)"/)?.[1];
          if (relValue) {
            (element as HTMLLinkElement).rel = relValue;
          }
        }
        document.head.appendChild(element);
      }

      element.setAttribute(attribute, value);
    };

    updateTag('link[rel="canonical"]', "href", canonicalUrl, "link");
    updateTag('meta[name="description"]', "content", description);

    updateTag('meta[property="og:title"]', "content", title);
    updateTag('meta[property="og:description"]', "content", description);
    updateTag('meta[property="og:url"]', "content", canonicalUrl);
    updateTag('meta[property="og:image"]', "content", fullOgImage);
    updateTag('meta[property="og:type"]', "content", ogType);
    updateTag('meta[property="og:site_name"]', "content", "Sotsek Advocacia");

    updateTag('meta[name="twitter:title"]', "content", title);
    updateTag('meta[name="twitter:description"]', "content", description);
    updateTag('meta[name="twitter:url"]', "content", canonicalUrl);
    updateTag('meta[name="twitter:image"]', "content", fullOgImage);

    // Per-page JSON-LD (e.g. Article schema). Removed on unmount.
    const injectedScripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(JSONLD_ATTR, "true");
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
        injectedScripts.push(script);
      });
    }

    return () => {
      injectedScripts.forEach((s) => s.remove());
    };
  }, [title, description, canonicalUrl, fullOgImage, ogType, jsonLd]);

  return null;
}
